import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Gift, Download, Trophy, Flag, Check, X } from "lucide-react";

export default function AdminReferrals() {
  const [d, setD] = useState(null);
  const [flagged, setFlagged] = useState([]);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/admin/referrals"), api.get("/admin/referrals/flagged")]);
    setD(a.data); setFlagged(b.data.flagged || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Loader />;

  const act = async (rid, kind) => {
    try { await api.post(`/admin/referrals/${rid}/${kind}`); toast.success(kind === "approve" ? "Approved" : "Rejected"); load(); }
    catch { toast.error("Action failed"); }
  };
  const grant = async (uid) => {
    const reason = window.prompt("Grant a referral reward (365 days). Reason?", "Manual reward");
    if (reason === null) return;
    try { await api.post(`/admin/referrals/user/${uid}/reward`, { reason }); toast.success("Reward granted"); load(); }
    catch { toast.error("Failed"); }
  };

  const stats = [
    { label: "Total", value: d.total, c: "#2563EB" },
    { label: "Valid", value: d.valid, c: "#0B6B3A" },
    { label: "Pending", value: d.pending, c: "#EA580C" },
    { label: "Flagged", value: d.flagged, c: "#DB2777" },
    { label: "Rewards given", value: d.rewards_given, c: "#7C3AED" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold flex items-center gap-2"><Gift className="w-7 h-7 text-thali-saffron" /> Referrals</h1>
        <a href={`${API}/admin/referrals/export`} className="btn-ghost text-sm" data-testid="ref-export"><Download className="w-4 h-4" /> Export Excel</a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="card-thali p-4" data-testid={`ref-stat-${i}`}>
            <div className="text-xs text-thali-ink/60">{s.label}</div>
            <div className="font-num font-extrabold text-2xl" style={{ color: s.c }}>{s.value}</div>
          </div>
        ))}
      </div>

      {flagged.length > 0 && (
        <div className="card-thali" data-testid="ref-flagged">
          <div className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><Flag className="w-5 h-5 text-red-500" /> Flagged for review</div>
          <div className="space-y-2">
            {flagged.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl bg-red-50 p-2.5" data-testid={`ref-flag-${r.id}`}>
                <div className="text-sm"><b>{r.referrer_name}</b> ← {r.name} {r.mobile_masked}</div>
                <div className="flex gap-1">
                  <button onClick={() => act(r.id, "approve")} className="w-9 h-9 rounded-lg bg-green-100 text-green-700 grid place-items-center" data-testid={`ref-approve-${r.id}`}><Check className="w-4 h-4" /></button>
                  <button onClick={() => act(r.id, "reject")} className="w-9 h-9 rounded-lg bg-red-100 text-red-600 grid place-items-center" data-testid={`ref-reject-${r.id}`}><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-thali">
        <div className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-thali-turmericdark" /> Top Referrers</div>
        {d.leaders.length === 0 ? <p className="text-sm text-thali-ink/50">No valid referrals yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead><tr className="bg-thali-cream text-thali-ink/70"><th className="text-left p-2 pl-4">#</th><th className="text-left p-2">Name</th><th className="p-2">Code</th><th className="p-2">Valid</th><th className="p-2">Rewards</th><th className="p-2 pr-4">Action</th></tr></thead>
              <tbody>
                {d.leaders.map((l, i) => (
                  <tr key={l.id} className="border-t border-thali-turmeric/10" data-testid={`ref-leader-${l.id}`}>
                    <td className="p-2 pl-4 font-bold">{i + 1}</td>
                    <td className="p-2">{l.name} <span className="text-xs text-thali-ink/50">{l.mobile}</span></td>
                    <td className="p-2 text-center font-mono">{l.code}</td>
                    <td className="p-2 text-center font-num font-bold">{l.valid}</td>
                    <td className="p-2 text-center">{l.rewards}</td>
                    <td className="p-2 pr-4 text-center"><button onClick={() => grant(l.id)} className="btn-ghost text-xs px-2 py-1" data-testid={`ref-grant-${l.id}`}><Gift className="w-3.5 h-3.5" /> Reward</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
